/* =============================================================
   GenericOutgoingProfile.jsx — Profile for any outgoing student
   -------------------------------------------------------------
   Dynamic profile page for outgoing students. Looks up the
   student by URL parameter (:id) from Supabase.

   Process stepper uses the university's configured steps from
   process_steps (set up in Settings → Exchange Process).
   Current step is persisted in the student_process table.

   ✓ DONE (backend integration):
   - Student data from Supabase (useStudentById)
   - Process steps from process_steps table (useProcessConfig)
   - Current step from student_process table (useStudentProcess)
   - Documents section: still uses DocumentContext (localStorage)
     → will be replaced when Supabase Storage is connected
   ============================================================= */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLocalNote }        from '../hooks/useLocalNote';
import { useStudentDocuments } from '../context/DocumentContext';
import { useProcessConfig }    from '../hooks/useProcessConfig';
import { useProcessTemplates } from '../hooks/useProcessTemplates';
import { useStudentProcess }   from '../hooks/useStudentProcess';
import { useStudentById }      from '../hooks/useStudentById';
import { usePartnerById }      from '../hooks/usePartnerById';
import { useTaskCompletions }  from '../hooks/useTaskCompletions';
import { useSemesters }        from '../hooks/useSemesters';
import { getStudentCategory, buildDocList, resolveProcessTemplate, DEGREE_LEVELS } from '../lib/studentCategory';


/* ─── STATUS STYLES ──────────────────────────────────────────── */
const STATUS_STYLES = {
  'Departed':                   'bg-slate-100 text-slate-600',
  'Partner Contacted':          'bg-amber-100 text-amber-700',
  'Nominated':                  'bg-blue-100 text-blue-700',
  'Applied':                    'bg-slate-200 text-slate-600',
  'Returned':                   'bg-slate-100 text-slate-500',
  'Learning Agreement Pending': 'bg-orange-100 text-orange-700',
  'Learning Agreement Signed':  'bg-emerald-100 text-emerald-700',
};


// BASE_DOCUMENTS removed — document list is now built dynamically from
// process_steps + document_types (same source as the student portal).

function statusConfig(status) {
  switch (status) {
    case 'uploaded':
    case 'approved':  return { label: 'Approved',          style: 'bg-emerald-100 text-emerald-700', icon: 'check_circle'    };
    case 'pending':   return { label: 'Pending Review',    style: 'bg-amber-100 text-amber-700',     icon: 'hourglass_empty' };
    case 'rejected':  return { label: 'Changes Requested', style: 'bg-red-100 text-red-600',         icon: 'cancel'          };
    default:          return { label: 'Missing',           style: 'bg-red-100 text-red-700',         icon: 'cancel'          };
  }
}


/* ─── HORIZONTAL STEPPER ─────────────────────────────────────── */
function HorizontalStepper({ steps, currentIndex, isFullyComplete = false, children }) {
  const n = steps.length;
  if (n === 0) return null;

  const halfCell  = 100 / (2 * n);
  const lineSpan  = 100 - 2 * halfCell;
  const progFrac  = isFullyComplete ? 1 : (currentIndex <= 0 ? 0 : currentIndex / (n - 1));
  const progWidth = progFrac * lineSpan;
  const pct       = isFullyComplete ? 100 : Math.round((currentIndex / n) * 100);
  const displayCount = isFullyComplete ? n : currentIndex;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">route</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Process Status</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{displayCount} of {n} phases completed</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            isFullyComplete ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-50 text-indigo-600'
          }`}>{pct}%</span>
        </div>
      </div>
      <div className="relative flex">
        <div className="absolute bg-slate-100 rounded-full"
          style={{ top: '16px', left: `${halfCell}%`, width: `${lineSpan}%`, height: '2px' }} />
        {progWidth > 0 && (
          <div className="absolute rounded-full transition-all duration-500 overflow-hidden"
            style={{ top: '16px', left: `${halfCell}%`, width: `${progWidth}%`, height: '2px', backgroundColor: '#4343d5' }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)',
              width: '40%',
              animation: 'progress-shimmer 2.8s ease-in-out infinite',
            }} />
          </div>
        )}
        <style>{`
          @keyframes progress-shimmer {
            0%   { transform: translateX(-100%); }
            100% { transform: translateX(350%); }
          }
        `}</style>
        {steps.map((step, i) => {
          const isDone    = isFullyComplete || i < currentIndex;
          const isCurrent = !isFullyComplete && i === currentIndex;
          return (
            <div key={step.id ?? i} className="flex-1 flex flex-col items-center">
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                isDone    ? 'bg-indigo-600 border-indigo-600' :
                isCurrent ? 'bg-white border-indigo-600'      :
                            'bg-white border-slate-200'
              }`}>
                {isDone ? (
                  <span className="material-symbols-outlined text-white text-[14px]">check</span>
                ) : isCurrent ? (
                  <>
                    <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-30" />
                    <div className="w-3 h-3 bg-indigo-600 rounded-full" />
                  </>
                ) : (
                  <span className="text-[10px] font-bold text-slate-300">{i + 1}</span>
                )}
              </div>
              <div className="mt-2 text-center px-0.5 w-full">
                <p className={`text-[11px] font-semibold leading-tight ${
                  isDone ? 'text-slate-600' : isCurrent ? 'text-indigo-700' : 'text-slate-300'
                }`}>{step.label}</p>
                {isCurrent && (
                  <p className="text-[10px] mt-0.5 text-indigo-500 font-medium">● Current</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {children && <div className="mt-5 space-y-2">{children}</div>}
    </div>
  );
}


/* ─── GENERIC OUTGOING PROFILE COMPONENT ────────────────────── */
export default function GenericOutgoingProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Student data from Supabase (cached across navigations)
  const { student, loading: studentLoading, updateStudent } = useStudentById(id, 'outgoing');

  // Inline edit state for Student Information card
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoSaving,  setInfoSaving]  = useState(false);
  const [infoError,   setInfoError]   = useState(null);
  const [infoDraft,   setInfoDraft]   = useState({});

  const startEditInfo = () => {
    setInfoDraft({
      name:                   student?.name ?? '',
      email:                  student?.email ?? '',
      destination_university: student?.dest === '—' ? '' : (student?.dest ?? ''),
      country:                student?.country ?? '',
      degree_level:           student?.degreeLevel       ?? '',
      degree_level_custom:    student?.degreeLevelCustom ?? '',
      process_template_id:    student?.processTemplateId ?? '',
      semester_id:            student?.semesterId        ?? '',
    });
    setInfoError(null);
    setEditingInfo(true);
  };

  const saveInfoEdit = async () => {
    setInfoSaving(true);
    setInfoError(null);
    try {
      await updateStudent({
        name:                   infoDraft.name.trim() || null,
        email:                  infoDraft.email.trim() || null,
        destination_university: infoDraft.destination_university.trim() || null,
        country:                infoDraft.country.trim() || null,
        degree_level:           infoDraft.degree_level || null,
        degree_level_custom:    infoDraft.degree_level === 'other'
                                  ? (infoDraft.degree_level_custom.trim() || null)
                                  : null,
        process_template_id:    infoDraft.process_template_id || null,
        semester_id:            infoDraft.semester_id || null,
      });
      setEditingInfo(false);
    } catch (err) {
      setInfoError(err.message ?? 'Save failed');
    } finally {
      setInfoSaving(false);
    }
  };

  // Partner university linked to this student
  const { partner } = usePartnerById(student?.partnerId);

  // Exchange terms for the semester dropdown
  const { semesters } = useSemesters();

  // All templates for this university — resolve the one that applies to this student
  const { templates: allTemplates, loading: templatesLoading } = useProcessTemplates();
  const outgoingTemplates = allTemplates.filter(t => t.type === 'outgoing');
  const resolvedTemplate  = student ? resolveProcessTemplate(outgoingTemplates, student) : null;

  // Process steps for the resolved template
  const { steps, loading: stepsLoading } = useProcessConfig(resolvedTemplate?.id ?? null);

  // Current step from student_process table
  const { currentStep, processLoading, advance, revert, goToStep } = useStudentProcess(id);

  // Admin notes
  const { note, setNote, save, savedAt, justSaved } = useLocalNote(`outgoing_${id}`);

  // Documents from Supabase Storage via useStudentDocuments
  const {
    documents, docsLoading,
    uploadDocument, approveDocument, rejectDocument,
    viewDocument, downloadDocument,
    reloadStudentDocuments,
  } = useStudentDocuments(id);

  // Approve / reject action state — tracks which doc is being actioned
  // and shows an inline error if the DB update fails.
  const [docAction,  setDocAction]  = useState(null); // { id, type: 'approving'|'rejecting' }
  const [docError,   setDocError]   = useState(null); // { id, message }
  const [openMenuId, setOpenMenuId] = useState(null); // id del doc con menu ⋮ aperto
  const menuRef = useRef(null);

  // Chiude il menu ⋮ quando si clicca fuori
  useEffect(() => {
    if (!openMenuId) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  const handleApprove = async (docId) => {
    setDocAction({ id: docId, type: 'approving' });
    setDocError(null);
    try {
      await approveDocument(docId);
      // Fire-and-forget: non blocca il finally, il reload avviene in background
      reloadStudentDocuments();
    } catch (err) {
      setDocError({ id: docId, message: err?.message ?? JSON.stringify(err) });
    } finally {
      setDocAction(null);
    }
  };

  const handleReject = async (docId) => {
    setDocAction({ id: docId, type: 'rejecting' });
    setDocError(null);
    try {
      await rejectDocument(docId);
      reloadStudentDocuments();
    } catch (err) {
      setDocError({ id: docId, message: err?.message ?? JSON.stringify(err) });
    } finally {
      setDocAction(null);
    }
  };

  // Task completions for checkbox items
  const { completions: taskCompletions } = useTaskCompletions(id);

  // Build doc list early (before early returns) so the useEffect below
  // is never called after a conditional return — React rules of hooks.
  const studentCategory = getStudentCategory(student?.exchangeType, student?.euStatus);
  const docs = buildDocList(steps, documents, studentCategory, taskCompletions);

  /* ── Auto-advance / auto-revert ───────────────────────────── */
  // 1. If a doc in a PREVIOUS step is rejected → revert to that step.
  // 2. If ALL docs in the CURRENT step are approved → advance to next.
  useEffect(() => {
    if (processLoading || stepsLoading || docsLoading) return;
    if (docs.length === 0 || steps.length === 0) return;

    // 1. Find earliest step before currentStep with a rejected doc
    for (let i = 0; i < currentStep; i++) {
      const stepDocs = docs.filter(d => d.stepIndex === i);
      if (stepDocs.length > 0 && stepDocs.some(d => d.effectiveStatus === 'rejected')) {
        goToStep(i);
        return;
      }
    }

    // 2. Auto-advance if all docs in currentStep are approved
    const currentStepDocs = docs.filter(d => d.stepIndex === currentStep);
    if (
      currentStepDocs.length > 0 &&
      currentStepDocs.every(d => ['uploaded', 'approved', 'task_done'].includes(d.effectiveStatus)) &&
      currentStep < steps.length - 1
    ) {
      advance(steps.length - 1);
    }
  }, [docs, currentStep, steps.length, processLoading, stepsLoading, docsLoading, advance, goToStep]);

  // Spinner solo al primo caricamento — se abbiamo già dati, li mostriamo subito
  const isLoading = (studentLoading   && !student)
                 || (templatesLoading && allTemplates.length === 0)
                 || (stepsLoading     && steps.length === 0 && resolvedTemplate !== null)
                 || (processLoading   && !student);

  /* ── Loading spinner ──────────────────────────────────────── */
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  /* ── Student not found ────────────────────────────────────── */
  if (!student) {
    return (
      <Layout>
        <button onClick={() => navigate('/outgoing')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group">
          <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
          Outgoing Students
        </button>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[48px]">person_off</span>
          <p className="text-slate-400 mt-3">Student not found.</p>
        </div>
      </Layout>
    );
  }

  /* ── No process configured ────────────────────────────────── */
  if (steps.length === 0 && !stepsLoading && !templatesLoading) {
    return (
      <Layout>
        <button onClick={() => navigate('/outgoing')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group">
          <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
          Outgoing Students
        </button>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[48px]">route</span>
          <p className="text-slate-500 font-medium mt-3">No outgoing process configured yet.</p>
          <p className="text-slate-400 text-sm mt-1">Go to <strong>Settings → Exchange Process</strong> to set up your workflow.</p>
          <button onClick={() => navigate('/settings')}
            className="mt-4 px-5 py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors">
            Go to Settings
          </button>
        </div>
      </Layout>
    );
  }

  /* ── Main render ──────────────────────────────────────────── */
  const canAdvance    = currentStep < steps.length - 1;
  const currentNote   = steps[currentStep]?.description ?? null;
  // Status label and style now reflect the current process step, not the
  // static students.status DB field — this stays in sync automatically
  // as the IO advances the stepper.
  const currentStepLabel = steps[currentStep]?.label ?? student.status;
  const statusStyle = !canAdvance
    ? 'bg-emerald-100 text-emerald-700'   // all phases done
    : 'bg-indigo-100 text-indigo-700';    // in progress

  const approvedCount = docs.filter(d => ['uploaded', 'approved', 'task_done'].includes(d.effectiveStatus)).length;
  const pendingCount  = docs.filter(d => d.effectiveStatus === 'pending').length;

  // Fully complete: on the last step AND every doc/task in it is done
  const _lastStepDocs = docs.filter(d => d.stepIndex === steps.length - 1);
  const isFullyComplete = steps.length > 0
    && currentStep === steps.length - 1
    && _lastStepDocs.length > 0
    && _lastStepDocs.every(d => ['uploaded', 'approved', 'task_done'].includes(d.effectiveStatus));

  // Next deadline: earliest upcoming deadline among incomplete docs/tasks
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const _incompleteDocs = docs.filter(d =>
    d.deadline_date &&
    !['uploaded', 'approved', 'task_done'].includes(d.effectiveStatus)
  );
  const _nextDate = _incompleteDocs
    .map(d => new Date(d.deadline_date + 'T00:00:00'))
    .filter(d => d >= _today)
    .sort((a, b) => a - b)[0] ?? null;
  const nextDeadline = _nextDate
    ? _nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const docsByStep = steps.map((step, i) => ({
    stepIndex: i,
    stepLabel: step.label,
    docs:      docs.filter(d => d.stepIndex === i),
  }));

  return (
    <Layout>
      <button onClick={() => navigate('/outgoing')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group">
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        Outgoing Students
      </button>

      {/* Page header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold text-[#0b1c30]">{student.name}</h1>
          <p className="text-[#464555] mt-2 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[16px]">location_on</span>
            {student.dest}{student.country ? `, ${student.country.replace(/[^\x00-\x7F]/g, '').trim()}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Preview Student Portal — opens the student's view of the portal */}
          <button
            onClick={() => navigate(`/student?as=${id}&from=${encodeURIComponent(location.pathname)}`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100 transition-colors"
          >
            <span className="material-symbols-outlined text-[15px]">preview</span>
            Preview Student Portal
          </button>
          <span className="px-3 py-1 rounded-full border border-slate-200 text-sm text-slate-600 font-medium">Exchange 2026/27</span>
          {student.urgent && (
            <span className="px-3 py-1 rounded-full border border-red-200 bg-red-50 text-xs font-semibold text-red-600 flex items-center gap-1">
              <span className="material-symbols-outlined text-[13px]">priority_high</span>Urgent
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusStyle}`}>{currentStepLabel}</span>
        </div>
      </div>

      {/* Process stepper — uses dynamic steps from Supabase */}
      <HorizontalStepper steps={steps} currentIndex={currentStep} isFullyComplete={isFullyComplete}>
        {currentNote && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
            <span className="material-symbols-outlined text-indigo-500 text-[16px] flex-shrink-0 mt-0.5">info</span>
            <p className="text-xs text-indigo-700 leading-relaxed">
              <span className="font-semibold">{steps[currentStep].label}: </span>{currentNote}
            </p>
          </div>
        )}
        {isFullyComplete ? (
          <div className="flex items-center gap-2.5 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <span className="material-symbols-outlined text-emerald-500 text-[16px]">verified</span>
            <p className="text-xs font-semibold text-emerald-700">Exchange completed — all phases done.</p>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
            <span className="material-symbols-outlined text-slate-400 text-[16px]">auto_awesome</span>
            <p className="text-xs text-slate-500">Steps advance automatically when all items in the current step are completed.</p>
          </div>
        )}
      </HorizontalStepper>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-6">

          {/* Student Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">person</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Student Information</h2>
              </div>
              {!editingInfo
                ? <button onClick={startEditInfo} className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-indigo-600 transition-colors">
                    <span className="material-symbols-outlined text-[15px]">edit</span>Edit
                  </button>
                : <div className="flex items-center gap-2">
                    {infoError && <p className="text-xs text-red-500">{infoError}</p>}
                    <button onClick={() => setEditingInfo(false)} disabled={infoSaving} className="text-xs font-medium text-slate-400 hover:text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
                    <button onClick={saveInfoEdit} disabled={infoSaving} className="flex items-center gap-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
                      {infoSaving && <span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>}
                      Save
                    </button>
                  </div>
              }
            </div>
            <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-5">
              {/* Full Name */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Full Name</p>
                {editingInfo
                  ? <input value={infoDraft.name} onChange={e => setInfoDraft(d => ({...d, name: e.target.value}))} className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  : <p className="text-sm text-[#0b1c30] font-medium">{student.name}</p>
                }
              </div>
              {/* Destination University */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Destination University</p>
                {editingInfo
                  ? <input value={infoDraft.destination_university} onChange={e => setInfoDraft(d => ({...d, destination_university: e.target.value}))} className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  : <p className="text-sm text-[#0b1c30] font-medium">{student.dest}</p>
                }
              </div>
              {/* Nationality */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Nationality</p>
                {editingInfo
                  ? <input value={infoDraft.country} onChange={e => setInfoDraft(d => ({...d, country: e.target.value}))} className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                  : <p className="text-sm text-[#0b1c30] font-medium">{student.country || '—'}</p>
                }
              </div>
              {/* Status (read-only) */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Status</p>
                <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle}`}>{currentStepLabel}</span>
              </div>
              {/* Next Deadline (read-only) */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Next Deadline</p>
                <p className={`text-sm font-medium flex items-center gap-1.5 ${student.urgent ? 'text-red-600' : 'text-[#0b1c30]'}`}>
                  <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_month</span>
                  {nextDeadline}
                </p>
              </div>
              {/* Email */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Email</p>
                {editingInfo
                  ? <input type="email" value={infoDraft.email} onChange={e => setInfoDraft(d => ({...d, email: e.target.value}))} className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200" placeholder="student@university.edu" />
                  : student.email
                    ? <a href={`mailto:${student.email}`} className="text-sm text-indigo-600 hover:underline font-medium">{student.email}</a>
                    : <p className="text-sm text-slate-400">—</p>
                }
              </div>
              {/* Degree Level */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Degree Level</p>
                {editingInfo ? (
                  <div className="space-y-1.5">
                    <select
                      value={infoDraft.degree_level}
                      onChange={e => setInfoDraft(d => ({...d, degree_level: e.target.value}))}
                      className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                    >
                      <option value="">Not specified</option>
                      {DEGREE_LEVELS.map(dl => (
                        <option key={dl.value} value={dl.value}>{dl.label}</option>
                      ))}
                    </select>
                    {infoDraft.degree_level === 'other' && (
                      <input
                        value={infoDraft.degree_level_custom}
                        onChange={e => setInfoDraft(d => ({...d, degree_level_custom: e.target.value}))}
                        placeholder="Specify degree type…"
                        className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-[#0b1c30] font-medium">
                    {student.degreeLevel
                      ? (student.degreeLevel === 'other'
                          ? (student.degreeLevelCustom || 'Other')
                          : (DEGREE_LEVELS.find(d => d.value === student.degreeLevel)?.label ?? student.degreeLevel))
                      : <span className="text-slate-400">—</span>}
                  </p>
                )}
              </div>
              {/* Exchange Term */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Exchange Term</p>
                {editingInfo ? (
                  <select
                    value={infoDraft.semester_id}
                    onChange={e => setInfoDraft(d => ({...d, semester_id: e.target.value}))}
                    className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                  >
                    <option value="">— Not assigned —</option>
                    {semesters.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-[#0b1c30] font-medium">
                    {semesters.find(s => s.id === student.semesterId)?.name
                      ?? <span className="text-slate-400">—</span>}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">folder_open</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Documents</h2>
              </div>
              <div className="flex items-center gap-2">
                {pendingCount > 0 && (
                  <span className="flex items-center gap-1 text-xs font-semibold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                    <span className="material-symbols-outlined text-[13px]">notifications</span>
                    {pendingCount} pending review
                  </span>
                )}
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                  approvedCount === docs.length ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                }`}>{approvedCount} of {docs.length} Complete</span>
              </div>
            </div>
            <div className="p-4 space-y-1">
              {docsByStep.map(group => {
                const isLocked  = group.stepIndex > currentStep;
                const isCurrent = group.stepIndex === currentStep;
                const isDone    = group.stepIndex < currentStep || (isFullyComplete && group.stepIndex === currentStep);
                return (
                  <div key={group.stepIndex}>
                    <div className={`flex items-center gap-2 px-2 pt-4 pb-2 ${group.stepIndex === 0 ? 'pt-2' : ''}`}>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isDone ? 'bg-indigo-100 text-indigo-700' : isCurrent ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
                      }`}>Step {group.stepIndex + 1}</span>
                      <span className={`text-xs font-semibold ${isDone ? 'text-slate-500' : isCurrent ? 'text-indigo-700' : 'text-slate-300'}`}>
                        {group.stepLabel}
                      </span>
                      {isDone   && <span className="material-symbols-outlined text-indigo-400 text-[14px]">check_circle</span>}
                      {isLocked && <span className="material-symbols-outlined text-slate-300 text-[14px]">lock</span>}
                    </div>
                    {group.docs.length === 0 && (
                      <div className="mx-2 mb-2 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                        <p className="text-xs text-slate-400 italic">No documents required for this step.</p>
                      </div>
                    )}
                    {group.docs.map(doc => {
                      const isTask    = doc.item_type === 'task';
                      const isTaskDone = doc.effectiveStatus === 'task_done';
                      const cfg       = statusConfig(doc.effectiveStatus);
                      const isPending = doc.effectiveStatus === 'pending';
                      return (
                        <div key={doc.junctionId ?? doc.name}
                          className={`flex items-center justify-between px-4 py-3.5 rounded-xl mx-2 mb-1 transition-colors ${
                            isLocked  ? 'opacity-40' :
                            isPending ? 'bg-amber-50 border border-amber-100' :
                                        'hover:bg-slate-50'
                          }`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <span className="material-symbols-outlined text-[16px] text-slate-500">{doc.icon}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-[#0b1c30]">{doc.name}</span>
                              {!isTask && isPending && doc.upload && (
                                <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[11px]">attach_file</span>
                                  {doc.upload.fileName} · uploaded {new Date(doc.upload.uploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              )}
                              {doc.deadline_date && (() => {
                                const isComplete = ['uploaded','approved','task_done'].includes(doc.effectiveStatus);
                                return (
                                  <p className={`text-xs mt-0.5 flex items-center gap-1 font-medium ${
                                    isComplete                                             ? 'text-emerald-500' :
                                    doc.deadlineDays !== null && doc.deadlineDays <= 7   ? 'text-red-500'     :
                                    doc.deadlineDays !== null && doc.deadlineDays <= 14  ? 'text-amber-500'   :
                                    'text-slate-400'
                                  }`}>
                                    <span className="material-symbols-outlined text-[12px]">event</span>
                                    Due {new Date(doc.deadline_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {!isComplete && (
                                      <>
                                        {doc.deadlineDays !== null && doc.deadlineDays <= 0  && ' · Overdue'}
                                        {doc.deadlineDays !== null && doc.deadlineDays === 1 && ' · Tomorrow'}
                                        {doc.deadlineDays !== null && doc.deadlineDays > 1 && doc.deadlineDays <= 14 && ` · ${doc.deadlineDays} days left`}
                                      </>
                                    )}
                                  </p>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Task badge */}
                            {isTask && (
                              <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                isTaskDone ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                              }`}>
                                <span className="material-symbols-outlined text-[14px]">{isTaskDone ? 'check_circle' : 'hourglass_empty'}</span>
                                {isTaskDone ? 'Confirmed by student' : 'Awaiting student'}
                              </div>
                            )}
                            {/* Document status badge */}
                            {!isTask && (
                              <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.style}`}>
                                <span className="material-symbols-outlined text-[14px]">{cfg.icon}</span>
                                {cfg.label}
                              </div>
                            )}
                            {!isTask && (doc.effectiveStatus === 'missing' || doc.effectiveStatus === 'rejected') && !isLocked && (
                              <label className="cursor-pointer text-xs font-medium text-indigo-600 border border-indigo-200 px-2.5 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1">
                                <span className="material-symbols-outlined text-[14px]">upload</span>Upload
                                <input type="file" className="hidden" onChange={e => {
                                  const file = e.target.files[0];
                                  if (file) uploadDocument(doc.name, file);
                                }} />
                              </label>
                            )}
                            {/* View + Download — visibili ogni volta che c'è un file */}
                            {!isTask && doc.upload && (
                              <>
                                <button
                                  onClick={() => viewDocument(doc.upload)}
                                  className="text-xs font-medium text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1"
                                >
                                  <span className="material-symbols-outlined text-[14px]">open_in_new</span>View
                                </button>
                                <button
                                  onClick={() => downloadDocument(doc.upload)}
                                  className="text-xs font-medium text-slate-600 border border-slate-200 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1"
                                >
                                  <span className="material-symbols-outlined text-[14px]">download</span>Download
                                </button>
                              </>
                            )}
                            {/* Azioni — disponibili finché lo step non è bloccato e c'è un file */}
                            {!isTask && doc.upload && !isLocked && (() => {
                              const busy   = docAction?.id === doc.upload.id;
                              const status = doc.effectiveStatus;

                              if (status === 'pending') {
                                // Pending → pulsanti espliciti Approve + Reject
                                return (
                                  <>
                                    {docError?.id === doc.upload.id && (
                                      <span className="text-xs text-red-500 max-w-[120px] truncate" title={docError.message}>
                                        {docError.message}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleApprove(doc.upload.id)}
                                      disabled={busy}
                                      className="text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1 disabled:opacity-50"
                                    >
                                      <span className={`material-symbols-outlined text-[14px] ${busy && docAction.type === 'approving' ? 'animate-spin' : ''}`}>
                                        {busy && docAction.type === 'approving' ? 'progress_activity' : 'check'}
                                      </span>
                                      Approve
                                    </button>
                                    <button
                                      onClick={() => handleReject(doc.upload.id)}
                                      disabled={busy}
                                      className="text-xs font-medium text-red-600 border border-red-200 bg-red-50 px-2.5 py-1.5 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-1 disabled:opacity-50"
                                    >
                                      <span className={`material-symbols-outlined text-[14px] ${busy && docAction.type === 'rejecting' ? 'animate-spin' : ''}`}>
                                        {busy && docAction.type === 'rejecting' ? 'progress_activity' : 'close'}
                                      </span>
                                      Reject
                                    </button>
                                  </>
                                );
                              }

                              if (status === 'approved' || status === 'rejected') {
                                // Approved / Rejected → menu ⋮ per cambiare decisione
                                const isOpen = openMenuId === doc.upload.id;
                                return (
                                  <div className="relative" ref={isOpen ? menuRef : null}>
                                    {busy ? (
                                      <span className="material-symbols-outlined text-[18px] text-slate-400 animate-spin">progress_activity</span>
                                    ) : (
                                      <button
                                        onClick={() => setOpenMenuId(isOpen ? null : doc.upload.id)}
                                        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                                        title="Cambia decisione"
                                      >
                                        <span className="material-symbols-outlined text-[18px]">more_vert</span>
                                      </button>
                                    )}
                                    {isOpen && (
                                      <div className="absolute right-0 top-8 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[140px]">
                                        {docError?.id === doc.upload.id && (
                                          <p className="text-xs text-red-500 px-3 py-1.5">{docError.message}</p>
                                        )}
                                        {status === 'approved' && (
                                          <button
                                            onClick={() => { setOpenMenuId(null); handleReject(doc.upload.id); }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                          >
                                            <span className="material-symbols-outlined text-[16px]">close</span>
                                            Mark as Rejected
                                          </button>
                                        )}
                                        {status === 'rejected' && (
                                          <button
                                            onClick={() => { setOpenMenuId(null); handleApprove(doc.upload.id); }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors"
                                          >
                                            <span className="material-symbols-outlined text-[16px]">check</span>
                                            Mark as Approved
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              }

                              return null;
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col-span-4 space-y-6">
          {/* Admin Notes */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notes</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Admin Notes</h2>
              </div>
              {savedAt && <p className="text-[11px] text-slate-400 flex items-center gap-1"><span className="material-symbols-outlined text-[12px]">schedule</span>Saved {savedAt}</p>}
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400 mb-3">Internal notes — visible only to university staff.</p>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
                placeholder="Add a note about this student's process..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition" />
              <button onClick={save} className={`mt-3 w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                justSaved ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'border border-slate-200 text-[#464555] hover:bg-slate-50'
              }`}>
                <span className="material-symbols-outlined text-[16px]">{justSaved ? 'check_circle' : 'save'}</span>
                {justSaved ? 'Saved!' : 'Save Note'}
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Actions</h2>
            </div>
            <div className="p-5 space-y-3">
              <button className="w-full py-2.5 border border-indigo-300 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">send</span>Send Reminder to Partner
              </button>
              <button className="w-full py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">mail</span>Contact Student
              </button>
            </div>
          </div>

          {/* IO Contacts */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">IO Contacts</h2>
              </div>
              {partner && (
                <a href={`/partners/${partner.id}`} className="text-xs font-medium text-indigo-500 hover:text-indigo-700 flex items-center gap-1 transition-colors">
                  View profile
                  <span className="material-symbols-outlined text-[13px]">arrow_forward</span>
                </a>
              )}
            </div>
            <div className="p-5 space-y-4">
              {!partner ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="material-symbols-outlined text-[16px]">link_off</span>
                  <p className="text-xs">No partner university linked to this student.</p>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Partner University</p>
                    <p className="text-sm font-medium text-[#0b1c30]">{partner.name}</p>
                    {partner.country && <p className="text-xs text-slate-400">{partner.country}</p>}
                  </div>
                  {(partner.contact_emails ?? []).length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No contacts added on the partner profile yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {(partner.contact_emails ?? []).map((c, i) => (
                        <div key={i}>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{c.role || 'Contact'}</p>
                          <p className="text-sm font-medium text-[#0b1c30]">{c.name}</p>
                          {c.email && <a href={`mailto:${c.email}`} className="text-xs text-indigo-600 hover:underline">{c.email}</a>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
